'use client'

import type { Route } from 'next'
import { useRouter } from 'next/navigation'
import { LineChart } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty'
import { Skeleton } from '@/components/ui/skeleton'
import { routes } from '@/config/routes'
import {
  CaseAnalysis,
  PendingRequestCard,
  toMockCaseRecord,
  toMockHoldings,
  toMockTrades,
  useCaseHoldings,
  useCaseTrades,
  useMyCaseForFlow,
} from '@/features/cases'
import { InvestorShell } from '@/features/investor/components/investor-shell'

/** Ported from `nivex-design/apps/nivex-web-v3/src/pages/dashboard/DashboardPortfolio.tsx`. */
export function InvestorPortfolioView() {
  const router = useRouter()
  const query = useMyCaseForFlow('audit')
  const holdingsQuery = useCaseHoldings(query.data?.id)
  const tradesQuery = useCaseTrades(query.data?.id, 1, 50)

  return (
    <InvestorShell
      active="portfolio"
      title="Portfolio"
      description="Everything we've pulled from your CAS, in one place."
      actions={
        query.summary?.advisorName ? (
          <Badge variant="secondary" className="hidden max-w-56 truncate sm:inline-flex">
            {query.summary.advisorName} is looking after this
          </Badge>
        ) : null
      }
    >
      {query.isListLoading || (query.summary?.caseStatus === 'published' && query.isLoading) ? (
        <Skeleton className="h-40 w-full" />
      ) : query.summary && query.summary.caseStatus !== 'published' ? (
        <PendingRequestCard
          caseRecord={{
            id: query.summary.id,
            flow: 'audit',
            reference: query.summary.reference,
            status: query.summary.caseStatus,
          }}
        />
      ) : query.summary && query.data ? (
        <CaseAnalysis
          caseRecord={toMockCaseRecord(query.data)}
          injectedHoldings={
            holdingsQuery.data ? toMockHoldings(holdingsQuery.data, query.data.id) : []
          }
          injectedTrades={
            tradesQuery.data ? toMockTrades(tradesQuery.data.items, query.data.id) : []
          }
        />
      ) : (
        <Empty className="border border-dashed py-10">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <LineChart />
            </EmptyMedia>
            <EmptyTitle>Want us to audit your portfolio?</EmptyTitle>
            <EmptyDescription>
              Upload your CAS statement and a financial expert will review your holdings.
            </EmptyDescription>
          </EmptyHeader>
          <Button onClick={() => router.push(routes.investor.request('audit') as Route)}>
            Get started
          </Button>
        </Empty>
      )}
    </InvestorShell>
  )
}
